import { useMemo } from "react"
import { useSelector } from "react-redux"
import {
    checkboxColDefCreator,
    companyAbsenceTypeColDefCreator,
    employeeColDefCreator,
    numberColDefCreator,
    projectColDefCreator,
    stringColDefCreator,
} from "./standard-col-def-creators"
import { TableIds } from "../../../common/table-identifier-utils"
import { Absence, ReduxState, tFeatures, TimekeepingEntry } from "../../../common/types"
import { tSourceData, tResourceObject, ResourceObject } from "../../../dashboard-data/types"
import { getObjectId } from "../../../common/ag-grid-ts-utils"
import { tGroupKeyInfo } from "../types"
import { getFlagEnabled } from "../../../getFlagValue"
import { EmployeeHolidayRecord, EmployeePTORecord } from "../../../cached-data/types"
import { IconPalmTree, IconPalmTreeFilled, iconSizeL } from "@rhumbix/rmbx_design_system_web"

/**
 * Extends employeesInContext with the employee from lockedColumns so that an employee with
 * PTO/holiday data but no TK rows is still considered in-context for the PTO Requests tab.
 * Kept separate from the main employeesInContext to avoid changing behavior for cohort
 * loading, CI/CO diff checks, and child component contexts.
 *
 * @param employeesInContext - Employee IDs already derived from source data rows
 * @param lockedColumns - The modal's locked column group-key info
 */
export const usePTORequestEmployeesFromContext = (
    employeesInContext: number[],
    lockedColumns: tGroupKeyInfo[] | null
): number[] => {
    return useMemo(() => {
        if (!getFlagEnabled("WA-9197-pto-accruals")) return []

        const lockedEmployee = lockedColumns?.find(c => c.colDef.field === "/employee")
        if (!lockedEmployee?.value) return employeesInContext
        const lockedId = getObjectId(lockedEmployee.value)
        return lockedId && !employeesInContext.includes(lockedId)
            ? [...employeesInContext, lockedId]
            : employeesInContext
    }, [employeesInContext, lockedColumns])
}

export type PTORequestsTableRow = ResourceObject<{
    /** The source request record — either an EmployeePTO or EmployeeHoliday. */
    request: EmployeePTORecord | EmployeeHolidayRecord
    /** The matched absence or TK entry that fulfills the request, or null if unfulfilled. */
    match: Absence | TimekeepingEntry | null
    /** Frontend-computed: true when a matching absence/TK entry was found in the current view. */
    fulfilled: boolean
    date: string
    work_shift_id: number | null
    absence_type: number | null
    cost_code: number | null
    /** Project ID from the matched TK entry; populated only for COST_CODE rows. */
    project: number | null
}>

/**
 * Builds the combined row data for the PTO Requests tab by joining employeePTO and employeeHolidays
 * (from Redux entities) with absences and timekeepingEntries (from the modal's local source data).
 *
 * Fulfillment is computed on the frontend: a record is considered fulfilled if a matching
 * absence (for ABSENCE_TYPE buckets) or TK entry (for COST_CODE buckets) is found in the
 * current view. All records are always shown; the fulfillment icon indicates their status.
 *
 * @param localSourceData - The modal's local source data (absences and timekeepingEntries)
 * @param employeesInContext - Array of employee IDs present in the open modal
 * @param date - The date string (YYYY-MM-DD) for the currently open modal
 */
export const usePTORequestsTableRows = (
    localSourceData: tSourceData,
    employeesInContext: number[],
    date: string | null | undefined
): tResourceObject[] => {
    const employeePTO = useSelector((state: ReduxState) => state.entities.employeePTO?.objects ?? {})
    const employeeHolidays = useSelector((state: ReduxState) => state.entities.employeeHolidays?.objects ?? {})
    const costCodes = useSelector((state: ReduxState) => state.entities.costCodes?.objects ?? {})

    return useMemo(() => {
        if (!getFlagEnabled("WA-9197-pto-accruals") || !date) return []

        const employeeSet = new Set(employeesInContext)
        const absences = (localSourceData.absences ?? []) as ResourceObject<Absence>[]
        const timekeepingEntries = (localSourceData.timekeepingEntries ?? []) as ResourceObject<TimekeepingEntry>[]

        // Build lookup: "employeeId-date" → absences for that employee+date
        const absencesByEmployeeDate = new Map<string, ResourceObject<Absence>[]>()
        absences.forEach(absence => {
            const employeeId = getObjectId(absence.employee)
            if (!employeeSet.has(employeeId)) return
            const key = `${employeeId}-${absence.date}`
            const existing = absencesByEmployeeDate.get(key) ?? []
            existing.push(absence)
            absencesByEmployeeDate.set(key, existing)
        })

        // Build lookup: "employeeId-date" → all TK entries for that employee+date
        const tkEntriesByEmployeeDate = new Map<string, ResourceObject<TimekeepingEntry>[]>()
        timekeepingEntries.forEach(entry => {
            const employeeId = getObjectId(entry.employee)
            if (!employeeSet.has(employeeId)) return
            const key = `${employeeId}-${entry.date}`
            const existing = tkEntriesByEmployeeDate.get(key) ?? []
            existing.push(entry)
            tkEntriesByEmployeeDate.set(key, existing)
        })

        const ptoRecords = Object.values(employeePTO).filter(r => employeeSet.has(r.employee) && r.date === date)
        const holidayRecords = Object.values(employeeHolidays).filter(
            r => employeeSet.has(r.employee) && r.date === date
        )

        const rows = [...ptoRecords, ...holidayRecords].reduce((rows, record) => {
            const key = `${record.employee}-${record.date}`

            if (record.pto_bucket_type === "ABSENCE_TYPE") {
                const matchedAbsence = (absencesByEmployeeDate.get(key) ?? []).find(
                    a => getObjectId(a.absence_type) === record.absence_type_id
                )
                rows.push({
                    request: record,
                    match: matchedAbsence ?? null,
                    fulfilled: !!matchedAbsence,
                    date: record.date,
                    cost_code: null,
                    project: null,
                    absence_type: matchedAbsence?.absence_type ?? record.absence_type_id ?? null,
                    work_shift_id: matchedAbsence?.work_shift_id ?? null,
                    gridId: record.id,
                })
            } else {
                // COST_CODE: find a TK entry whose cost code maps to the same PTO bucket and whose
                // ST minutes meet or exceed the requested hours. Compare in integer minutes to avoid
                // floating-point precision issues.
                const matchedEntry = (tkEntriesByEmployeeDate.get(key) ?? []).find(e => {
                    const ccId = getObjectId(e.cost_code)
                    const cc = ccId != null ? costCodes[ccId] : undefined
                    const entrySt = e.adjusted_minutes_st ?? 0
                    return (
                        cc?.pto_bucket_id === record.pto_bucket_id &&
                        record.hours != null &&
                        entrySt >= Math.round(record.hours * 60)
                    )
                })
                rows.push({
                    request: record,
                    match: matchedEntry ?? null,
                    fulfilled: !!matchedEntry,
                    date: record.date,
                    cost_code: matchedEntry ? getObjectId(matchedEntry.cost_code) ?? null : null,
                    project: matchedEntry
                        ? matchedEntry.project ??
                          (typeof matchedEntry.cost_code === "object"
                              ? matchedEntry.cost_code?.project?.id ?? null
                              : null)
                        : null,
                    absence_type: null,
                    work_shift_id: matchedEntry?.work_shift_id ?? null,
                    gridId: record.id,
                })
            }

            return rows
        }, [] as PTORequestsTableRow[])

        return rows
    }, [employeePTO, employeeHolidays, costCodes, localSourceData, employeesInContext, date])
}

/**
 * Returns the column and grid configuration for the PTO Requests tab in the weekly TK detail modal.
 * This tab is a read-only reference view of PTO and holiday requests for the employees in context,
 * showing their fulfillment status and details. A "Fulfill Requests" row action allows the user to
 * copy request details as placeholder rows into the Timekeeping and Absences tabs.
 *
 * The Project, Requested Hours, and Absence Type columns are hidden by default; the modal
 * shows them selectively based on which PTO bucket types are present in the current row data.
 */
export const getPTORequestsTable = (_featureFlags: tFeatures): Record<string, unknown> => {
    return {
        tableName: "PTO Requests",
        id: TableIds.PTORequestsModal,
        navId: "pto-requests",
        resources: ["employeePTO", "employeeHolidays"],
        colDefs: [
            checkboxColDefCreator({}),
            {
                colId: "pto_fulfillment_status",
                headerName: "Fulfilled?",
                field: "/fulfilled",
                width: 70,
                sortable: false,
                filter: false,
                cellRenderer: ({ data }: { data?: PTORequestsTableRow }) => {
                    if (!data) return null
                    return (
                        <div style={{ width: iconSizeL }}>
                            {data.fulfilled ? (
                                <IconPalmTreeFilled title="Fulfilled" className="toolbar-btn-icon" />
                            ) : (
                                <IconPalmTree title="Unfulfilled" className="toolbar-btn-icon" />
                            )}
                        </div>
                    )
                },
            },
            {
                ...employeeColDefCreator({}),
                field: "/request/employee",
            },
            stringColDefCreator({
                headerName: "Reason",
                field: "/reason",
                valueGetter: ({ data }: { data: PTORequestsTableRow }) => {
                    return (data.request as EmployeeHolidayRecord).name ?? "Employee Request"
                },
                width: 180,
            }),
            stringColDefCreator({
                headerName: "Bucket",
                field: "/request/pto_bucket_name",
                width: 180,
            }),
            {
                ...projectColDefCreator({}),
                colId: "pto_project",
                field: "/project",
                hide: true,
                required: false,
            },
            {
                ...numberColDefCreator({
                    headerName: "Requested Hours",
                    field: "/request/hours",
                    hide: true,
                    width: 150,
                }),
                colId: "pto_requested_hours",
            },
            {
                ...companyAbsenceTypeColDefCreator({}),
                colId: "pto_absence_type",
                headerName: "Absence Type",
                hide: true,
            },
        ],
        gridSettings: {
            defaultColDef: {
                filter: false,
                editable: false,
            },
            rowSelection: "multiple",
            suppressRowClickSelection: true,
            // Only unfulfilled rows may be selected; fulfilled ones are already satisfied.
            isRowSelectable: (rowNode: { data?: PTORequestsTableRow }) => !rowNode.data?.fulfilled,
        },
        otherSettings: {
            filterByLockedColumns: true,
            hideLockedColumns: true,
            isInModalTab: true,
            suppressExportButton: true,
            highlightErrorRows: true,
            buttons: {
                cell: [],
                row: [
                    {
                        label: "Fulfill Requests",
                        icon: "fulfillPTO",
                        action: "fulfillPTORequests",
                        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                        disabled: (context: { selectedRows: PTORequestsTableRow[] }) =>
                            !context.selectedRows.some(r => !r.fulfilled),
                        tooltip: () =>
                            "Add timekeeping entries to the Timekeeping and Absences tabs" +
                            " according to the details of the selected requests.",
                    },
                ],
                table: [
                    {
                        label: "Fulfill Requests",
                        icon: "fulfillPTO",
                        action: "fulfillAllPTORequests",
                        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                        disabled: (_context: unknown, sourceData: Record<string, PTORequestsTableRow[]>) =>
                            !Object.values(sourceData)
                                .flat()
                                .some(r => !r.fulfilled),
                        tooltip: () =>
                            "Add timekeeping entries to the Timekeeping and Absences tabs" +
                            " for all PTO requests shown in this tab.",
                    },
                ],
            },
        },
    }
}
